import { refreshWaiting } from './waiting'
import { supabase } from './supabase'
import { withPhotoBylines } from './photoRoom'

/**
 * 152. The Gallery in three roles, and photo review.
 *
 * ── THREE VIEWS OF ONE SET ──────────────────────────────────────────────────
 * The browser (photo.browse, or photo_policy.browse_for_everyone) scrolls the
 * Gallery at feed size and searches captions — photo_gallery_browse, which
 * records nothing. The document user (photo.room) has the grid, filters, the
 * place NAME and downloads (lib/photoRoom). The admin (photo.room +
 * photo.location) also has the exact point, camera, original, who downloaded,
 * the tag list and the review queue. Every function here refuses whoever may
 * not use it (rule 12); the page's own checks are courtesy.
 *
 * ── REVIEW ──────────────────────────────────────────────────────────────────
 * Anybody who can see a photo may ask for a review. Asking never removes it;
 * it pauses downloads while open. "I don't consent" takes the photo out of
 * every Gallery use at once, and no review can overrule it — only "that isn't
 * you" (identityCorrected) or the person withdrawing lifts it.
 */

export type ReviewReason = 'no_consent' | 'unsafe' | 'identifiable' | 'other'
export const REVIEW_REASONS: ReviewReason[] = ['no_consent', 'unsafe', 'identifiable', 'other']

export type ReviewOutcome = 'keep' | 'restrict' | 'remove' | 'moderation'
export const REVIEW_OUTCOMES: ReviewOutcome[] = ['keep', 'restrict', 'remove', 'moderation']

/** The uses a reviewer can take away. Contests are the uploader's (147). */
export type RestrictableUse = 'marketing' | 'sales' | 'other'
export const RESTRICTABLE_USES: RestrictableUse[] = ['marketing', 'sales', 'other']

/** A note is required with "Something else" (the server says so too). */
export function reviewNoteRequired(reasons: ReviewReason[]): boolean {
  return reasons.includes('other')
}

/* ── the browser view ─────────────────────────────────────────────────────── */

export type BrowsePhoto = {
  path: string
  contentId: string
  tenantId: string
  caption: string | null
  width: number | null
  height: number | null
  postedAt: string
  uploaderName: string
  /** 300. The byline its post went out under, or null. */
  bylineName: string | null
}
export type BrowseSort = 'newest' | 'oldest'
export const BROWSE_PAGE = 24

export async function browseGallery(
  q: { query?: string; sort?: BrowseSort } = {},
  offset = 0,
  limit = BROWSE_PAGE,
): Promise<{ photos: BrowsePhoto[]; total: number }> {
  const { data, error } = await supabase.rpc('photo_gallery_browse', {
    p_query: q.query?.trim() || null,
    p_sort: q.sort ?? 'newest',
    p_limit: limit,
    p_offset: offset,
  })
  if (error) throw error
  const rows = (data ?? []) as Record<string, unknown>[]
  return {
    photos: await withPhotoBylines(rows.map((r) => ({
      path: r.storage_path as string,
      contentId: r.content_id as string,
      tenantId: r.tenant_id as string,
      caption: (r.caption as string | null) ?? null,
      width: (r.width as number | null) ?? null,
      height: (r.height as number | null) ?? null,
      postedAt: r.posted_at as string,
      uploaderName: (r.uploader_name as string) ?? '',
      bylineName: null,
    }))),
    // The server counts the whole match before its limit (rule 1).
    total: rows.length ? Number(rows[0].total_count) : 0,
  }
}

/* ── what a document user sees beside a photo ─────────────────────────────── */

export type GalleryDetails = {
  countryCode: string | null
  country: string | null
  region: string | null
  locality: string | null
  underReview: boolean
}

export async function galleryDetails(paths: string[]): Promise<Map<string, GalleryDetails>> {
  const out = new Map<string, GalleryDetails>()
  if (paths.length === 0) return out
  const { data, error } = await supabase.rpc('photo_gallery_details', { p_paths: paths })
  if (error) throw error
  for (const r of (data ?? []) as Record<string, unknown>[]) {
    out.set(r.storage_path as string, {
      countryCode: (r.country_code as string | null) ?? null,
      country: (r.country as string | null) ?? null,
      region: (r.region as string | null) ?? null,
      locality: (r.locality as string | null) ?? null,
      underReview: r.under_review === true,
    })
  }
  return out
}

/** "Antofagasta, Antofagasta Region, Chile" — town, region, country. */
export function placeName(d: Pick<GalleryDetails, 'locality' | 'region' | 'country'> | null | undefined): string | null {
  if (!d) return null
  const parts = [d.locality, d.region, d.country].filter((x): x is string => !!x && !!x.trim())
  return parts.length ? parts.join(', ') : null
}

/* ── asking ───────────────────────────────────────────────────────────────── */

export type MyReviewState = {
  isMine: boolean
  canRequest: boolean
  myReviewId: string | null
  myReasons: ReviewReason[]
  /** Only for a document user or admin; null for anybody else. */
  underReview: boolean | null
}

export async function myPhotoReviewState(paths: string[]): Promise<Map<string, MyReviewState>> {
  const out = new Map<string, MyReviewState>()
  if (paths.length === 0) return out
  const { data, error } = await supabase.rpc('my_photo_review_state', { p_paths: paths })
  if (error) throw error
  for (const r of (data ?? []) as Record<string, unknown>[]) {
    out.set(r.storage_path as string, {
      isMine: r.is_mine === true,
      canRequest: r.can_request === true,
      myReviewId: (r.my_review_id as string | null) ?? null,
      myReasons: ((r.my_reasons as string[] | null) ?? []) as ReviewReason[],
      underReview: typeof r.under_review === 'boolean' ? r.under_review : null,
    })
  }
  return out
}

export async function requestPhotoReview(path: string, reasons: ReviewReason[], note: string): Promise<string> {
  const { data, error } = await supabase.rpc('request_photo_review', {
    p_storage_path: path,
    p_reasons: reasons,
    p_note: note.trim() || null,
  })
  if (error) throw error
  return data as string
}

export async function withdrawPhotoReview(id: string): Promise<void> {
  const { error } = await supabase.rpc('withdraw_photo_review', { p_review_id: id })
  if (error) throw error
}

/* ── the queue ────────────────────────────────────────────────────────────── */

export type ReviewState = 'open' | 'closed' | 'all'

export type QueueItem = {
  id: string
  tenantId: string
  path: string
  contentId: string | null
  caption: string | null
  posterName: string | null
  reasons: ReviewReason[]
  note: string | null
  requestedAt: string
  /** Gallery admins only. A person a request was handed to never sees who asked. */
  requesterName: string | null
  assignedTo: string | null
  assignedToName: string | null
  assignedNote: string | null
  outcome: ReviewOutcome | null
  restrictedUses: RestrictableUse[]
  identityCorrected: boolean
  outcomeNote: string | null
  decidedAt: string | null
  decidedByName: string | null
  withdrawnAt: string | null
  /** The consent reason is still taking the photo out of every Gallery use. */
  consentStanding: boolean
  moderationOpen: boolean
  access: 'admin' | 'assignee'
}

export async function loadReviewQueue(
  state: ReviewState = 'open',
  path: string | null = null,
  offset = 0,
  limit = 50,
): Promise<{ items: QueueItem[]; total: number }> {
  const { data, error } = await supabase.rpc('photo_review_queue', {
    p_state: state,
    p_storage_path: path,
    p_limit: limit,
    p_offset: offset,
  })
  if (error) throw error
  const rows = (data ?? []) as Record<string, unknown>[]
  return {
    items: rows.map((r) => ({
      id: r.review_id as string,
      tenantId: r.tenant_id as string,
      path: r.storage_path as string,
      contentId: (r.content_id as string | null) ?? null,
      caption: (r.caption as string | null) ?? null,
      posterName: (r.poster_name as string | null) ?? null,
      reasons: ((r.reasons as string[] | null) ?? []) as ReviewReason[],
      note: (r.note as string | null) ?? null,
      requestedAt: r.requested_at as string,
      requesterName: (r.requester_name as string | null) ?? null,
      assignedTo: (r.assigned_to as string | null) ?? null,
      assignedToName: (r.assigned_to_name as string | null) ?? null,
      assignedNote: (r.assigned_note as string | null) ?? null,
      outcome: (r.outcome as ReviewOutcome | null) ?? null,
      restrictedUses: ((r.restricted_uses as string[] | null) ?? []) as RestrictableUse[],
      identityCorrected: r.identity_corrected === true,
      outcomeNote: (r.outcome_note as string | null) ?? null,
      decidedAt: (r.decided_at as string | null) ?? null,
      decidedByName: (r.decided_by_name as string | null) ?? null,
      withdrawnAt: (r.withdrawn_at as string | null) ?? null,
      consentStanding: r.consent_standing === true,
      moderationOpen: r.moderation_open === true,
      access: r.access === 'admin' ? 'admin' : 'assignee',
    })),
    total: rows.length ? Number(rows[0].total_count) : 0,
  }
}

/** Hand to a named person (HLP-31), or back (person = null). */
export async function handPhotoReview(id: string, personId: string | null, note: string): Promise<void> {
  const { error } = await supabase.rpc('hand_photo_review', {
    p_review_id: id,
    p_person_id: personId,
    p_note: note.trim() || null,
  })
  if (error) throw error
  refreshWaiting() // 325: a count on the nav or the bell may have moved
}

export async function decidePhotoReview(
  id: string,
  d: { outcome: ReviewOutcome; restrictedUses?: RestrictableUse[]; identityCorrected?: boolean; note?: string },
): Promise<void> {
  const { error } = await supabase.rpc('decide_photo_review', {
    p_review_id: id,
    p_outcome: d.outcome,
    p_restricted_uses: d.outcome === 'restrict' ? d.restrictedUses ?? [] : null,
    p_identity_corrected: d.identityCorrected === true,
    p_note: d.note?.trim() || null,
  })
  if (error) throw error
  refreshWaiting() // 325: a count on the nav or the bell may have moved
}

/* ── the switch ───────────────────────────────────────────────────────────── */

export async function loadBrowseForEveryone(tenantId: string): Promise<boolean | null> {
  const { data, error } = await supabase
    .from('photo_policy')
    .select('browse_for_everyone')
    .eq('tenant_id', tenantId)
    .maybeSingle<{ browse_for_everyone: boolean }>()
  if (error) return null
  return data?.browse_for_everyone ?? false
}

export async function setBrowseForEveryone(on: boolean): Promise<void> {
  const { error } = await supabase.rpc('set_photo_policy', { p_browse_for_everyone: on })
  if (error) throw error
}

/* ── 170: thirty days to say "don't use it" ───────────────────────────────── */

/**
 * Until when the caller may pick "I'm in this photo and don't consent" for
 * each photo they can see and did not post: the post's publish time, or their
 * tag on it if later, plus the tenant's window (30 days). Every other reason
 * has no window. The server refuses a late consent request whatever the
 * dialog shows (request_photo_review); this is only so the dialog can say so.
 */
export type ConsentWindow = { until: string | null; open: boolean }

export async function consentWindows(paths: string[]): Promise<Map<string, ConsentWindow>> {
  const out = new Map<string, ConsentWindow>()
  if (paths.length === 0) return out
  const { data, error } = await supabase.rpc('photo_consent_windows', { p_paths: paths })
  if (error) throw error
  for (const r of (data ?? []) as Record<string, unknown>[]) {
    out.set(r.storage_path as string, {
      until: (r.consent_until as string | null) ?? null,
      open: r.consent_open === true,
    })
  }
  return out
}
